// WHAT A CURSOR KEY'S COLUMN CAN HOLD — the kind vocabulary a list read declares for
// each of its ordering keys, and the one verdict over it.
//
// The codec (`./cursor`) proves a token is well-formed: a known collection, an order,
// the right number of TAGGED values. It cannot know that slot 0 of a `created_at`
// order is a `timestamptz` and the tiebreak slot of `pull_requests` is a `uuid`. A
// forged token carrying a string where the column wants a uuid is well-formed, was
// admitted, and reached `lt(column, value)` — Postgres `22P02`, a 500 and an
// error-tracker event per request, where the honest answer is `invalid cursor`
// (plan 101 row 04-10). The same class #2053 closed for a SIBLING collection's
// token, one level down: the stamp says which collection, this says which types.
//
// The kinds are named after what the COLUMN holds, not after the codec's tags,
// because two of them share a tag: `text` and `uuid` are both a tagged string.
//
// The vocabulary is the kinds a LIVE ordering column has, and no more: a kind with
// no column behind it is an admit rule nobody has run against Postgres. A new
// ordering column of another type fails at module load in `@developerz/db`
// (`listColumns` throws on a type it has no kind for), and that is where its kind
// is added — here, with its rule, beside the column that needs it.

import type { ListCursorValue } from './types';

/** The column kinds a cursor key may address. */
export const LIST_KEY_KINDS = ['text', 'uuid', 'timestamp', 'integer'] as const;
export type ListKeyKind = (typeof LIST_KEY_KINDS)[number];

/**
 * The kinds of ONE collection's cursor keys: one per sortable grammar field, plus
 * the stable tiebreak every cursor ends on. Plain data, so the read that owns the
 * columns (`@developerz/db` `ListColumns.keyKinds`) can hand it to the route that parses
 * the query (`ListQuerySpec.keyKinds`) without either importing the other's world.
 */
export type ListCursorKeyKinds = {
  sort: Readonly<Record<string, ListKeyKind>>;
  tiebreak: ListKeyKind;
  /**
   * Every sort field whose column is NULLABLE, so whose cursor key may be `null`
   * (#2774) — every other slot, and the tiebreak always, refuses one as a key its
   * column cannot hold. Exactly the nullable columns: a sort key cannot state
   * `nonNullBy` (that is the tiebreak's alone, and `@developerz/db` `listColumns`
   * refuses it on a sort key), so no nullable sort column is left out. Derived from
   * the same columns as the kinds, and emitted only when non-empty: a collection
   * that orders by no nullable column carries exactly the shape it did before.
   */
  nullable?: readonly string[];
};

// Canonical 8-4-4-4-12, the only spelling the driver hands back and so the only
// one a cursor of ours carries. Postgres also reads braces and unhyphenated hex;
// a token carrying those was not minted here.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const INT4_MAX = 2_147_483_647;
const INT4_MIN = -2_147_483_648;
// `Date#toISOString` spells a year outside 0000–9999 with a sign and six digits,
// which Postgres does not read as a timestamp (`22009`), and it has no year 0000.
// So the admitted range is years 0001 through 9999. No row of ours is dated
// outside it, so a cursor naming such a date is not one of ours either.
//
// PARSED, not `Date.UTC(1, 0, 1)`: `Date.UTC` maps a year argument of 0–99 to
// 1900–1999, so that expression is 1901 — which is what this said until a review
// read it, refusing every date before the twentieth century.
const MIN_TIMESTAMP_MS = Date.parse('0001-01-01T00:00:00.000Z');
const MAX_TIMESTAMP_MS = Date.parse('9999-12-31T23:59:59.999Z');

/**
 * Can a column of `kind` be compared with `value`? Pure; `false` is a cursor the
 * read must refuse rather than bind.
 *
 * `null` is no kind's value, so every kind refuses it: whether a slot may hold
 * one is the collection's {@link ListCursorKeyKinds.nullable}, not the kind's.
 *
 * Each arm is a statement that would otherwise fail at the driver:
 *  - `text`      a string with no NUL — Postgres refuses `\u0000` in any text (`22021`);
 *  - `uuid`      a canonical uuid string (`22P02` otherwise);
 *  - `timestamp` a `Date` Postgres can parse (`22P02` for a string, `22009` for a
 *                year `toISOString` spells with a sign);
 *  - `integer`   a whole number inside int4 (`22P02` for a fraction, `22003` past it).
 */
export function listKeyKindAdmits(kind: ListKeyKind, value: ListCursorValue): boolean {
  switch (kind) {
    case 'text':
      return typeof value === 'string' && !value.includes('\u0000');
    case 'uuid':
      return typeof value === 'string' && UUID_RE.test(value);
    case 'timestamp': {
      if (!(value instanceof Date)) return false;
      const ms = value.valueOf();
      return ms >= MIN_TIMESTAMP_MS && ms <= MAX_TIMESTAMP_MS;
    }
    case 'integer':
      return (
        typeof value === 'number' &&
        Number.isInteger(value) &&
        value >= INT4_MIN &&
        value <= INT4_MAX
      );
  }
}
